import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  ChevronDown, 
  ChevronUp, 
  Search,
  HelpCircle,
  CreditCard,
  Users,
  Shield,
  FileText,
  Mail,
  ArrowRight
} from 'lucide-react';

const faqCategories = [
  {
    category: "Getting Started",
    icon: HelpCircle,
    items: [
      ["How do I get started with NewSlate?", "Contact us to set up your organization account. Once created, you'll receive admin credentials to log in. From there, you can add team members, create cases, and start managing your practice."],
      ["What are the system requirements?", "NewSlate is web-based and works in any modern browser (Chrome, Firefox, Safari, Edge). No software installation required."],
      ["How do I add team members?", "Go to Admin Users, click '+ Add User', fill in details, select role (Org Admin or Org User), and click Create."],
      ["Can I import existing client data?", "Yes! Use Bulk Import to upload a CSV. The system detects duplicates by A-Number or Name + DOB."]
    ]
  },
  {
    category: "Pricing & Billing",
    icon: CreditCard,
    items: [
      ["How much does NewSlate cost?", "$99 one-time setup fee per organization, plus $49 per user per month. All features included."],
      ["Are there long-term contracts?", "No, NewSlate is month-to-month. Cancel anytime with no fees."],
      ["Can I add or remove users anytime?", "Yes! Billing adjusts automatically on your next cycle."],
      ["What payment methods do you accept?", "All major credit cards (Visa, Mastercard, Amex). ACH available for annual plans."]
    ]
  },
  {
    category: "Client Portal",
    icon: Users,
    items: [
      ["What is the Client Portal?", "A secure self-service area where clients can view case status, update info, upload documents, and download forms."],
      ["How do I give a client portal access?", "Open the case, go to Client Portal tab, enter email, click Create. Share the 6-digit access code with your client."],
      ["Is the Client Portal included?", "Yes! Unlimited client portal access at no extra cost."],
      ["Can clients update their information?", "Yes, they can update personal info, contact details, employment, and emergency contacts. You get notified of changes."]
    ]
  },
  {
    category: "Security & Privacy",
    icon: Shield,
    items: [
      ["Is my data secure?", "Yes, all data is encrypted in transit (HTTPS) and at rest. We use industry-standard security practices."],
      ["Who can see my client data?", "Only users within your organization. Each organization's data is completely isolated."],
      ["Is there an audit trail?", "Yes, Super Admins can access complete audit logs of all administrative actions."]
    ]
  },
  {
    category: "Case Management",
    icon: FileText,
    items: [
      ["What info can I track for each case?", "Client name, DOB, country, service type, contact info, A-Number, USCIS receipt, biometrics date, status, documents, and history."],
      ["Can I upload documents?", "Yes, PDF, images (PNG, JPG), and Word docs. Max 10MB per file."],
      ["Can clients upload documents?", "Yes! Clients with portal access can upload directly. You get notified."]
    ]
  },
  {
    category: "Notifications",
    icon: Mail,
    items: [
      ["What automatic notifications are sent?", "Portal creation, status updates, document uploads, profile updates, and biometrics reminders (7, 3, 1 day before)."],
      ["How do biometrics reminders work?", "Set a biometrics date on a case, and clients with portal access get automatic email reminders."],
      ["Can I see what emails were sent?", "Yes, admins can view the Notifications page with send status and timestamps."]
    ]
  }
];

export default function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (catIdx, itemIdx) => {
    const key = `${catIdx}-${itemIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories = faqCategories.map(cat => ({
    ...cat,
    items: cat.items.filter(([q, a]) => 
      q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="NewSlate" className="h-10 object-contain" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/pricing"><Button variant="ghost" className="font-body">Pricing</Button></Link>
            <Link to="/login"><Button className="bg-primary text-white font-body">Sign In</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-body font-medium mb-6">
            Help Center
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-primary mb-6">
            Frequently Asked Questions
          </h1>
          <p className="font-body text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Find answers to common questions about NewSlate.
          </p>
          
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-lg font-body"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto px-6">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="font-body text-muted-foreground">No questions found.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {filteredCategories.map((cat, catIdx) => (
                <div key={catIdx}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <cat.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="font-display text-xl font-bold text-foreground">{cat.category}</h2>
                  </div>
                  
                  <div className="space-y-3">
                    {cat.items.map(([question, answer], itemIdx) => {
                      const isOpen = openItems[`${catIdx}-${itemIdx}`];
                      return (
                        <Card key={itemIdx} className="overflow-hidden">
                          <button
                            onClick={() => toggleItem(catIdx, itemIdx)}
                            className="w-full p-4 flex items-center justify-between text-left hover:bg-secondary/50 transition-colors"
                          >
                            <span className="font-body font-medium text-foreground pr-4">{question}</span>
                            {isOpen ? <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                          </button>
                          {isOpen && (
                            <CardContent className="pt-0 pb-4 px-4 border-t border-border">
                              <p className="font-body text-muted-foreground leading-relaxed">{answer}</p>
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-secondary/30 border-t border-border">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-2xl font-bold text-primary mb-4">Still Have Questions?</h2>
          <p className="font-body text-muted-foreground mb-8">Our support team is here to help.</p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a href="mailto:support@newslate.io" className="flex items-center gap-3 px-6 py-3 bg-card rounded-xl border border-border hover:border-primary transition-colors">
              <Mail className="w-5 h-5 text-primary" />
              <span className="font-body font-medium">support@newslate.io</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="font-body text-white/80 mb-8">Join immigration firms using NewSlate.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button size="lg" className="bg-card text-primary hover:bg-white/90 font-body">View Pricing</Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-body">
                Sign In <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-8 bg-card rounded-lg flex items-center justify-center">
              <span className="text-primary font-bold">N</span>
            </div>
            <span className="font-display text-lg font-bold">NewSlate</span>
          </div>
          <p className="font-body text-sm text-slate-400">© {new Date().getFullYear()} NewSlate. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
